/** Tutoring records for the treasurer, loaded from the server (online only for now). */
import type {
  LessonLookup,
  Tutor,
  TutoringClaim,
  TutoringEntry,
  TutoringPackage,
  TutoringPairing,
  TutoringSlot,
  TutoringStudent,
} from "@split/core";
import { api } from "./api.ts";
import { app } from "./store.svelte.ts";
import * as T from "../settings/tutoring.ts";

/** Lesson times are Los Angeles times, wherever this device is or whatever zone it's set to. */
export const TUTORING_TZ = "America/Los_Angeles";

/** Los Angeles time now, "2026-10-21T17:30": a lesson that has ended by then logs itself. */
export function localNow(d = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TUTORING_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const g = (t: string) => parts.find((x) => x.type === t)?.value ?? "00";
  return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
}

export type TutoringTable = "students" | "tutors" | "packages" | "entries" | "slots";

/** Lesson times and students' full names, for each lesson on a person's history. */
export function lessonLookup(
  slots: readonly TutoringSlot[],
  students: readonly { id: string; name: string }[] | Record<string, string>,
): LessonLookup {
  const slotOf = new Map(slots.map((s) => [s.id, s]));
  const names = Array.isArray(students)
    ? new Map(students.map((s) => [s.id, s.name]))
    : new Map(Object.entries(students));
  return { slot: (id) => slotOf.get(id), student: (id) => names.get(id) ?? "" };
}

class TutoringData {
  students = $state<TutoringStudent[]>([]);
  tutors = $state<Tutor[]>([]);
  packages = $state<TutoringPackage[]>([]);
  entries = $state<TutoringEntry[]>([]);
  slots = $state<TutoringSlot[]>([]);
  /** What each tutor is paid per hour for each student. */
  pairings = $state<TutoringPairing[]>([]);
  /** Tutors' takes and requests, oldest first. */
  claims = $state<TutoringClaim[]>([]);
  /** One-time fixes and what they did (newest first). */
  repairs = $state<{ id: string; ranAt: string; summary: string }[]>([]);
  loaded = $state(false);
  /** Everyone with an account, to link a student or tutor to theirs. */
  accounts = $state<{ id: string; name: string; role: string }[]>([]);
  error = $state("");

  async load() {
    try {
      const r = await api<{
        students: TutoringStudent[];
        tutors: Tutor[];
        packages: TutoringPackage[];
        entries: TutoringEntry[];
        slots: TutoringSlot[];
        pairings?: TutoringPairing[];
        claims?: TutoringClaim[];
        repairs?: { id: string; ranAt: string; summary: string }[];
      }>(`/api/tutoring?now=${localNow()}`);
      this.students = r.students;
      this.tutors = r.tutors;
      this.packages = r.packages;
      this.entries = r.entries;
      this.slots = r.slots;
      this.pairings = r.pairings ?? [];
      this.claims = r.claims ?? [];
      this.repairs = r.repairs ?? [];
      this.error = "";
      if (!this.accounts.length) {
        api<{ accounts: { id: string; name: string; role: string }[] }>("/api/admin/accounts")
          .then((a) => (this.accounts = a.accounts.filter((x) => x.role === "member")))
          .catch(() => {});
      }
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loaded = true;
    }
  }

  /**
   * A payout you logged by mistake: removed (paid = false), or brought back by Undo. Kept on the
   * server so Undo works, but nothing anywhere shows or counts it (packages/core tutorPayRows).
   */
  async setPaid(entryId: string, paid: boolean): Promise<void> {
    try {
      await api(`/api/tutoring/entries/${entryId}/paid`, { body: { paid } });
      await this.load();
      if (paid) app.toast(T.TUTOR_RESTORED_TOAST);
      else
        app.toast(T.TUTOR_REMOVED_TOAST, "info", {
          label: T.TUTOR_REMOVED_UNDO,
          run: () => void this.setPaid(entryId, true),
        });
    } catch (e) {
      app.toast((e as Error).message, "error");
    }
  }

  async add(
    table: TutoringTable,
    body: Record<string, unknown>,
    done?: string,
  ): Promise<string | null> {
    try {
      const r = await api<{ id: string }>(`/api/tutoring/${table}`, { body });
      await this.load();
      app.syncEverything(); // People's overall totals include tutoring
      if (done) app.toast(done);
      return r.id;
    } catch (e) {
      app.toast((e as Error).message, "error");
      return null;
    }
  }

  async update(table: TutoringTable, id: string, body: Record<string, unknown>, done?: string) {
    try {
      await api(`/api/tutoring/${table}/${id}`, { method: "PATCH", body });
      await this.load();
      app.syncEverything(); // People's overall totals include tutoring
      if (done) app.toast(done);
    } catch (e) {
      app.toast((e as Error).message, "error");
    }
  }

  /** A session done or not done (not done takes its logged hours back off). */
  async markDone(slotId: string, done: boolean, early = false) {
    try {
      await api(`/api/tutoring/slots/${slotId}/done`, { body: { done, early } });
      await this.load();
      app.syncEverything(); // People's overall totals include tutoring
      app.toast(done ? "Marked done" : "Marked not done");
    } catch (e) {
      app.toast((e as Error).message, "error");
    }
  }
  /** Any of Miles's own steps (lessons, edits, pay, requests): saved, then everything reloaded. */
  private async step(path: string, body: Record<string, unknown>, done?: string): Promise<boolean> {
    try {
      await api(path, { body });
      await this.load();
      app.syncEverything(); // People's overall totals include tutoring
      if (done) app.toast(done);
      return true;
    } catch (e) {
      app.toast((e as Error).message, "error");
      return false;
    }
  }
  /** Lessons on the calendar (a past one is logged as done at once). */
  addLessons(b: {
    studentId: string;
    tutorId: string | null;
    unclaimed: boolean;
    date: string;
    start: string;
    end: string;
    weeks: number;
    note: string;
  }) {
    return this.step("/api/tutoring/lessons", b);
  }
  /** Change a lesson on its card, booked or done. */
  editLesson(
    slotId: string,
    b: {
      startsAt: string;
      endsAt: string;
      studentId: string | null;
      tutorId: string | null;
      unclaimed: boolean;
      note: string;
    },
    done?: string,
  ) {
    return this.step(`/api/tutoring/slots/${slotId}/edit`, b, done);
  }
  /** A tutor's pay per hour for one student (and, with past, for lessons already taught). */
  setPairing(studentId: string, tutorId: string, payCents: number, past: boolean, done?: string) {
    return this.step("/api/tutoring/pairings", { studentId, tutorId, payCents, past }, done);
  }
  /** Approve a tutor's request (at the pay you set) or decline it. */
  decideClaim(claimId: string, approve: boolean, payCents: number | null, done?: string) {
    return this.step(`/api/tutoring/claims/${claimId}/decide`, { approve, payCents }, done);
  }

  async remove(table: TutoringTable, id: string) {
    try {
      await api(`/api/tutoring/${table}/${id}`, { method: "DELETE", body: {} });
      await this.load();
      app.syncEverything(); // People's overall totals include tutoring
    } catch (e) {
      app.toast((e as Error).message, "error");
    }
  }
}

export const tutoring = new TutoringData();
